import type { DateTime } from "luxon";
import { copyDayData, findScheduleSlot, type ScheduleSlot } from "./departmentWeeks";
import type { BaseNurseShift, Day, HospitalData, NurseInShift, WeekData } from "./models";

/**
 * The old list with `editedShift` swapped in on `date`, in its shift type's own row; unchanged when that day
 * isn't loaded there (a day outside the loaded weeks, whose cache refresh brings the edit in).
 */
export const withEditedShift = (data: HospitalData, date: DateTime, editedShift: NurseInShift, isActual: boolean): HospitalData => {
	const slot = findScheduleSlot(data.weeks, date, editedShift.shiftType);
	if (!slot) return data;
	const { day, newData } = copyDayData(data, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
	const replace = (shifts: NurseInShift[]) => shifts.map((shift) => (shift.id === editedShift.id ? { ...editedShift } : shift));
	if (isActual) day.nursesActual = day.nursesActual && replace(day.nursesActual);
	else day.nurses = replace(day.nurses);
	return newData;
};

/** What an hours, location or job edit sets on a shift. A field left out keeps its value. */
export type ShiftDetailsEdit = Partial<Pick<NurseInShift, "startTime" | "endTime" | "location" | "jobId" | "jobName" | "isPendingToUpdate">>;

type ShiftDetailsEditTarget = {
	shiftId: string;
	/** The day and shift type the caller has for the shift; the shift is looked for in that day's week. */
	date: DateTime;
	shiftType: string;
	/** The row the shift belongs in after the edit, on `date`. */
	newShiftType: string;
	isActual: boolean;
	edit: ShiftDetailsEdit;
};

const shiftsOf = (day: Day, isActual: boolean): BaseNurseShift[] | undefined => (isActual ? day.nursesActual : day.nurses);

const setShiftsOf = (day: Day, isActual: boolean, shifts: BaseNurseShift[]): void => {
	if (isActual) day.nursesActual = shifts;
	else day.nurses = shifts;
};

/** Where `shiftId` is listed in `week`, and the list it is in. */
const findShiftInWeek = (
	week: WeekData,
	shiftId: string,
	isActual: boolean,
): { shiftIndex: number; dayIndex: number; shift: BaseNurseShift; shifts: BaseNurseShift[] } | undefined => {
	for (const [shiftIndex, row] of week.weeklyShifts.entries()) {
		for (const [dayIndex, day] of row.days.entries()) {
			const shifts = shiftsOf(day, isActual);
			const shift = shifts?.find((candidate) => candidate.id === shiftId);
			if (shifts && shift) return { shiftIndex, dayIndex, shift, shifts };
		}
	}
	return undefined;
};

const isSameSlot = (slot: ScheduleSlot, other: ScheduleSlot): boolean =>
	slot.weekIndex === other.weekIndex && slot.shiftIndex === other.shiftIndex && slot.dayIndex === other.dayIndex;

/**
 * The old list with a shift's hours, location or job edited, moved to `newShiftType`'s row on `date` when the edit
 * changes its row and the date has one; unchanged when the shift isn't in `date`'s week. It copies what it changes
 * rather than editing in place: the staffing pages rebuild a week only when its arrays change.
 */
export const withShiftDetailsEdited = (
	data: HospitalData,
	{ shiftId, date, shiftType, newShiftType, isActual, edit }: ShiftDetailsEditTarget,
): HospitalData => {
	const slot = findScheduleSlot(data.weeks, date, shiftType);
	if (!slot) return data;
	// By id in the whole week: new hours can put the shift on another day than the one it is listed on now.
	const found = findShiftInWeek(data.weeks[slot.weekIndex], shiftId, isActual);
	if (!found) return data;
	const source = { weekIndex: slot.weekIndex, shiftIndex: found.shiftIndex, dayIndex: found.dayIndex };
	// The new type's own row for the date: rows can skip days, so the old row's position may be another date there.
	const target = findScheduleSlot(data.weeks, date, newShiftType);
	// With no row of the new type on the date the shift stays in its row, so it keeps that row's type.
	const edited = { ...found.shift, ...edit, ...(target ? { shiftType: newShiftType } : {}) };
	const targetShifts = target && shiftsOf(data.weeks[target.weekIndex].weeklyShifts[target.shiftIndex].days[target.dayIndex], isActual);
	const { day: sourceDay, newData } = copyDayData(data, source.weekIndex, source.shiftIndex, source.dayIndex);
	if (!target || !targetShifts || isSameSlot(source, target)) {
		setShiftsOf(
			sourceDay,
			isActual,
			found.shifts.map((shift) => (shift.id === shiftId ? edited : shift)),
		);
		return newData;
	}
	setShiftsOf(
		sourceDay,
		isActual,
		found.shifts.filter((shift) => shift.id !== shiftId),
	);
	const { day: targetDay, newData: movedData } = copyDayData(newData, target.weekIndex, target.shiftIndex, target.dayIndex);
	setShiftsOf(targetDay, isActual, [...targetShifts, edited]);
	return movedData;
};

/** One end of a move: the day, and the shift type there. */
export type MoveEnd = { date: DateTime; shiftType: string };

/** Where a pending move records its other end: the weekday, and the shift type's row as the old list's full week orders them. */
const movePositionOf = ({ date, shiftType }: MoveEnd, shiftTypes: readonly string[]): Pick<NurseInShift, "originalShift" | "originalDay"> => ({
	originalShift: shiftTypes.indexOf(shiftType),
	originalDay: date.weekdayISO,
});

/** `data` with `nurseId`'s shift on `from` marked as moving to `to`; unchanged when `from` isn't loaded there or has no such shift. */
export const withMoveSource = (
	data: HospitalData,
	from: MoveEnd,
	to: MoveEnd,
	nurseId: number,
	shiftTypes: readonly string[],
	isHospital: boolean,
): HospitalData => {
	const slot = findScheduleSlot(data.weeks, from.date, from.shiftType);
	if (!slot) return data;
	const { day, newData } = copyDayData(data, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
	const index = day.nurses.findIndex((shift) => shift.nurseId === nurseId);
	if (index === -1) return data;
	day.nurses = day.nurses.map((shift, at) =>
		at === index
			? { ...shift, isPending: true, isPendingToDelete: isHospital, isPendingToMove: true, isPublished: false, ...movePositionOf(to, shiftTypes) }
			: shift,
	);
	return newData;
};

/** `data` with `movedShift` added on `to`, recording `from` as where it moves from; unchanged when `to` isn't loaded there or already has the person. */
export const withMoveTarget = (data: HospitalData, to: MoveEnd, from: MoveEnd, movedShift: NurseInShift, shiftTypes: readonly string[]): HospitalData => {
	const slot = findScheduleSlot(data.weeks, to.date, to.shiftType);
	if (!slot) return data;
	const { day, newData } = copyDayData(data, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
	if (day.nurses.some((shift) => shift.nurseId === movedShift.nurseId)) return data;
	day.nurses = [...day.nurses, { ...movedShift, ...movePositionOf(from, shiftTypes) }];
	return newData;
};
